import {useEffect, useState} from 'react';

import {useGlobalState} from '@/state';
import {
	type CheckboxChangeHandler,
	type PointCloud as BasePointCloud,
	type VtkStateRef,
} from '@/types';

type PointCloud = BasePointCloud & {
	handleVisibilityChange: CheckboxChangeHandler;
};

export default function usePointCloudsVisibility({
	vtkState,
}: {
	vtkState: VtkStateRef;
}): {
	all: {
		isIndeterminate: boolean;
		isVisible: boolean;
		handleVisibilityChange: CheckboxChangeHandler;
	};
	areLoaded: boolean;
	individual: PointCloud[];
} {
	const {
		densityMap: {pointClouds, updatePointCloud},
		viewports: {volumeViewport},
	} = useGlobalState();
	const [individualPointClouds, setIndividualPointClouds] = useState<
		PointCloud[]
	>([]);
	const [isAllPointCloudsVisible, setIsAllPointCloudsVisible] =
		useState<boolean>(true);

	const areAllIndividualPointCloudsHidden = individualPointClouds.every(
		(cloud) => !cloud.isVisible,
	);
	const areAllIndividualPointCloudsVisible = individualPointClouds.every(
		(cloud) => cloud.isVisible,
	);

	// Synchronize all point clouds visibility when all or none of the individual
	// point clouds become visible
	useEffect(() => {
		if (areAllIndividualPointCloudsHidden) {
			setIsAllPointCloudsVisible(false);
		} else if (areAllIndividualPointCloudsVisible) {
			setIsAllPointCloudsVisible(true);
		}
	}, [individualPointClouds]);

	// Synchronize individual point clouds with global state
	useEffect(() => {
		setIndividualPointClouds(
			pointClouds.map((cloud) => ({
				...cloud,
				handleVisibilityChange({target: {checked: isVisible}}) {
					updatePointCloudVisibility({id: cloud.id, isVisible});
				},
			})),
		);
	}, [pointClouds]);

	const handleAllPointCloudsVisibilityChange: CheckboxChangeHandler = ({
		target: {checked: isVisible},
	}) => {
		for (const pointCloud of individualPointClouds) {
			updatePointCloudVisibility({id: pointCloud.id, isVisible});
		}

		setIsAllPointCloudsVisible(isVisible);
	};

	function updatePointCloudVisibility({
		id,
		isVisible,
	}: {
		id: string;
		isVisible: boolean;
	}) {
		const actorEntry = vtkState.current.pointCloudActors.find(
			(actor) => actor.id === id,
		);

		if (!actorEntry) return;

		actorEntry.actor.setVisibility(isVisible);
		volumeViewport?.render();

		updatePointCloud({id, isVisible});
	}

	return {
		all: {
			handleVisibilityChange: handleAllPointCloudsVisibilityChange,
			isIndeterminate:
				!areAllIndividualPointCloudsHidden &&
				!areAllIndividualPointCloudsVisible,
			isVisible: isAllPointCloudsVisible,
		},
		areLoaded: individualPointClouds.length > 0,
		individual: individualPointClouds,
	};
}
